'use client'

import { cn } from '@brew/ui/cn'
import type { MouseEvent, ReactNode } from 'react'
import { useAppLocale } from '@/components/i18n/locale-provider'
import { localizePath } from '@/lib/i18n/routing'
import { isPlainClick } from '@/lib/ui/plain-click'
import { MarketingContainer } from './marketing-container'
import {
  MARKETING_GUTTER_X,
  MARKETING_SECTION_HEADING,
  MARKETING_SECTION_Y,
} from './marketing-layout'
import { ProminentCtaButton } from './prominent-cta-button'
import { SectionTopDivider } from './section-divider'
import { useOpenAuthModal } from './use-auth-modal'

const SIGN_UP_HREF = '/sign-up'

/**
 * The copy comes from the caller, already in the reader's language
 * (`LandingClosingCta` translates it on the server).
 */
export type FinalCtaProps = {
  heading: ReactNode
  subheading?: ReactNode
  onCta?: () => void
  className?: string
  /** Optional decorative element rendered below the copy (e.g. a logo). */
  visual?: ReactNode
  /** Hairline top divider. Off where the section follows a divider-less page. */
  divider?: boolean
} /** The default action (sign up), labelled by the caller. */ & (
  | { ctaLabel: string; actions?: undefined }
  /** The caller's own actions instead of the default one. */
  | { actions: ReactNode; ctaLabel?: undefined }
)

export function FinalCta({
  heading,
  subheading,
  ctaLabel,
  actions,
  onCta,
  className,
  visual,
  divider = true,
}: FinalCtaProps) {
  const openAuth = useOpenAuthModal()
  const locale = useAppLocale()

  const handleClick = () => {
    if (onCta) {
      onCta()
      return
    }
    openAuth()
  }

  // The default CTA is a real link to sign-up in the reader's language, so it
  // works before hydration and without JavaScript; once hydrated a plain
  // click opens the modal (or goes to /home when signed in) in place, while a
  // Ctrl/Cmd/middle click still opens sign-up in a new tab.
  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainClick(event)) {
      return
    }
    event.preventDefault()
    handleClick()
  }

  return (
    <section
      className={cn(
        'relative w-full overflow-hidden',
        MARKETING_GUTTER_X,
        MARKETING_SECTION_Y,
        className
      )}
    >
      {divider ? <SectionTopDivider /> : null}
      <MarketingContainer
        size="prose"
        className="relative z-10 flex flex-col items-center gap-8 text-center md:gap-10"
      >
        <h2 className={cn(MARKETING_SECTION_HEADING, 'text-foreground')}>
          {heading}
        </h2>
        {actions ??
          (onCta ? (
            <ProminentCtaButton onClick={handleClick}>
              {ctaLabel}
            </ProminentCtaButton>
          ) : (
            <ProminentCtaButton
              href={localizePath(locale, SIGN_UP_HREF)}
              onClick={handleLinkClick}
            >
              {ctaLabel}
            </ProminentCtaButton>
          ))}
        {subheading ? (
          <p className="text-[15px] text-muted-foreground leading-6">
            {subheading}
          </p>
        ) : null}
        {visual}
      </MarketingContainer>
    </section>
  )
}
